import { useCallback, useEffect, useState } from "react";
import { supabase } from "./supabase";

export type SubscriptionStatus = "trialing" | "active" | "past_due" | "canceled" | null;

export interface SubscriptionState {
  status: SubscriptionStatus;
  trial_ends_at: string | null;
  current_period_end: string | null;
  square_subscription_id: string | null;
  loading: boolean;
  refetch: () => Promise<void>;
}

export function useSubscription(userId: string | undefined | null): SubscriptionState {
  const [status, setStatus] = useState<SubscriptionStatus>(null);
  const [trial_ends_at, setTrialEndsAt] = useState<string | null>(null);
  const [current_period_end, setCurrentPeriodEnd] = useState<string | null>(null);
  const [square_subscription_id, setSubId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!userId || !supabase) return;
    setLoading(true);
    try {
      const { data } = await supabase
        .from("profiles")
        .select("subscription_status, trial_ends_at, current_period_end, square_subscription_id")
        .eq("id", userId)
        .single();
      setStatus(data?.subscription_status ?? null);
      setTrialEndsAt(data?.trial_ends_at ?? null);
      setCurrentPeriodEnd(data?.current_period_end ?? null);
      setSubId(data?.square_subscription_id ?? null);
    } catch {
      // no profile row yet → null status is fine
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      refetch();
    } else {
      setStatus(null);
      setTrialEndsAt(null);
      setCurrentPeriodEnd(null);
      setSubId(null);
    }
  }, [userId, refetch]);

  return { status, trial_ends_at, current_period_end, square_subscription_id, loading, refetch };
}
